import { useEffect } from "react";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getOrderRef } from "../state/selectors";
import { isRunningOnClientSide } from "../../../../shared/util";
import {
    MAX_PAGES_VIEWED,
    MAX_PAGE_PATH_LENGTH,
    PAGES_VIEWED_COOKIE,
    PAGES_VIEWED_STORAGE_KEY,
    formatPageViewTime,
    isValidPageView,
    serializePagesViewed,
} from "../../../../shared/pagesViewed";
import { IPageView } from "../../../../models/server/meta";

interface StoredPageViews {
    orderRef?: string;
    pages: IPageView[];
}

const readStoredPageViews = (): StoredPageViews => {
    try {
        const raw = window.localStorage.getItem(PAGES_VIEWED_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : undefined;
        if (parsed && Array.isArray(parsed.pages)) {
            return { orderRef: parsed.orderRef, pages: parsed.pages.filter(isValidPageView) };
        }
    } catch {
        // Storage may be unavailable or hold malformed data; fall back to an empty history.
    }

    return { pages: [] };
};

/**
 * Records every visited page and mirrors it to a cookie so the server can persist it on the order.
 */
export const usePageViewTracker = (): void => {
    const { pathname, search } = useLocation();
    const orderRef = useSelector(getOrderRef);

    useEffect(() => {
        if (!isRunningOnClientSide()) {
            return;
        }

        const stored = readStoredPageViews();
        const isNewOrder = !!stored.orderRef && !!orderRef && stored.orderRef !== orderRef;
        const pageView: IPageView = {
            page: `${pathname}${search}`.slice(0, MAX_PAGE_PATH_LENGTH),
            time: formatPageViewTime(new Date()),
        };
        const pages = [...(isNewOrder ? [] : stored.pages), pageView].slice(-MAX_PAGES_VIEWED);

        try {
            window.localStorage.setItem(PAGES_VIEWED_STORAGE_KEY, JSON.stringify({ orderRef, pages }));
        } catch {
            // Private mode or an exceeded quota shouldn't break navigation.
        }

        Cookies.set(PAGES_VIEWED_COOKIE, serializePagesViewed(pages, orderRef), {
            expires: 1,
            path: "/",
            sameSite: "lax",
        });
    }, [pathname, search, orderRef]);
};
